import { useMemo } from 'react';
import ChapterSection from '../ChapterSection/ChapterSection';
import ChapterMap from '../ChapterMap/ChapterMap';
import type {
  Point,
  ReinfoLayerSummary,
  ReinfoPortfolio,
  RiskLevel,
} from '@/lib/api.types';
import {
  FLOOD_DEPTH_COLOR,
  FLOOD_DEPTH_LABEL,
  HAZARD_COLOR_FOR_RANK,
  HAZARD_GROUP_FOR_ENDPOINT,
  HAZARD_ICON_FOR,
  type HazardGroup,
} from '@/lib/chapters';
import type { GeometryLayer, LegendItem, MapSpec, MarkerSpec } from '@/lib/map-spec';
import type { LeadSpec, RowSpec } from '@/lib/report-content';
import { TILES, WATER_RASTER_URL } from '@/lib/tiles';
import { formatDistanceM } from '@/lib/format';
import {
  ChapterLead,
  CheckList,
  EmptyState,
  RawDetails,
  RowList,
  rankToStatusLabel,
} from './shared';

type Props = {
  portfolio: ReinfoPortfolio;
  site: Point;
};

const RANK_OPACITY: Record<RiskLevel, number> = {
  very_high: 0.42,
  high: 0.32,
  medium: 0.22,
  low: 0.12,
  unknown: 0.08,
};

const SUBMAP_HEIGHT = 220;

type SubmapBuild = {
  geometries: GeometryLayer[];
  markers: MarkerSpec[];
  legend: LegendItem[];
};

const HAZARD_GROUP_META: Record<HazardGroup, { caption: string; emptyMessage: string }> = {
  flood: {
    caption: '周辺の水害ハザード（浸水深で色分け：洪水・津波・高潮）',
    emptyMessage: 'この付近では水害ハザード区域は見つかりませんでした。',
  },
  landslide: {
    caption: '周辺の土砂災害区域',
    emptyMessage: 'この付近では土砂災害の指定区域は見つかりませんでした。',
  },
};

// 水害は種類ごとに地図を分ける。各マップのキャプション（浸水深で色分けする旨を明記）。
const WATER_MAP_CAPTION: Record<string, string> = {
  XKT026: '洪水浸水想定区域（想定最大規模）の浸水深',
  XKT027: '高潮浸水想定区域の浸水深',
  XKT028: '津波浸水想定の浸水深',
};

// 水害ハザードの面は国土地理院の公式ラスタータイル（浸水深で配色済み, z2-17）を重ねて描画する
// (URL は lib/tiles.ts)。バックエンドは判定（該当・浸水深）のため中心タイル1枚しか取得しないので、
// 広域の面はこのラスターが担う。これにより従来1回の診断で数十枚あった重いGeoJSONタイル取得が不要になる。
// 公式ラスターの浸水深配色に対応する凡例。FLOOD_DEPTH_COLOR は国交省の浸水深ランプに一致させてある。
const FLOOD_DEPTH_LEGEND: LegendItem[] = [1, 2, 3, 4, 5, 6].map((band) => ({
  color: FLOOD_DEPTH_COLOR[band],
  label: FLOOD_DEPTH_LABEL[band],
}));

function nearestMarker(layer: ReinfoLayerSummary, color: string): MarkerSpec | null {
  if (!layer.nearest_point) return null;
  const distance = layer.nearest_distance_m != null ? formatDistanceM(layer.nearest_distance_m) : null;
  return {
    point: layer.nearest_point,
    icon: HAZARD_ICON_FOR[layer.endpoint] ?? 'alert-triangle',
    color,
    title: distance ? `${layer.name} (${distance})` : layer.name,
  };
}

function buildHazardSubmap(layers: ReinfoLayerSummary[]): SubmapBuild {
  const geos: GeometryLayer[] = [];
  const mks: MarkerSpec[] = [];
  const seenRanks = new Set<RiskLevel>();
  for (const layer of layers) {
    const color = HAZARD_COLOR_FOR_RANK[layer.rank] ?? '#A8A8A8';
    const opacity = RANK_OPACITY[layer.rank] ?? 0.15;
    const polys = layer.feature_geometries ?? [];
    const polyLabels = layer.feature_labels ?? [];
    polys.forEach((g, i) => {
      const polyLabel = polyLabels[i] ?? layer.value_label ?? layer.name;
      geos.push({
        data: g,
        style: { stroke: false, fillColor: color, fillOpacity: opacity },
        label: `${layer.name}: ${polyLabel}`,
      });
    });
    if (layer.matched_count > 0) seenRanks.add(layer.rank);
    const marker = nearestMarker(layer, color);
    if (marker) mks.push(marker);
  }
  // ハザード強度のヒート凡例。§6.2 の状態語に揃え、要注意 / 要確認 / 参考 で示す
  // （旧「注意度高/低」を撤去。◯△の状態行とは別UIだが語彙を共通化して一貫させる）。
  const legend: LegendItem[] = [];
  if (seenRanks.has('very_high') || seenRanks.has('high')) {
    legend.push({ color: HAZARD_COLOR_FOR_RANK.high, label: '要注意' });
  }
  if (seenRanks.has('medium')) {
    legend.push({ color: HAZARD_COLOR_FOR_RANK.medium, label: '要確認' });
  }
  if (seenRanks.has('low')) {
    legend.push({ color: HAZARD_COLOR_FOR_RANK.low, label: '参考' });
  }
  return { geometries: geos, markers: mks, legend };
}

// 浸水深ヒートマップの塗り不透明度。深さは色で表現するため、レイヤ rank 別ではなく一定値にする。
const WATER_FILL_OPACITY = 0.55;

// 水害（洪水・高潮・津波）用サブマップ。各ポリゴンを浸水深ランク(1-6)で色分けし、
// ヒートマップ風に統一表示する。深さ情報が無いポリゴンはレイヤ rank の従来色にフォールバック。
type WaterCell = { data: GeoJSON.Geometry; band: number | null; color: string; label: string };

function buildWaterSubmap(layers: ReinfoLayerSummary[]): SubmapBuild {
  const cells: WaterCell[] = [];
  const mks: MarkerSpec[] = [];
  const bandsSeen = new Set<number>();
  for (const layer of layers) {
    const fallbackColor = HAZARD_COLOR_FOR_RANK[layer.rank] ?? '#A8A8A8';
    const polys = layer.feature_geometries ?? [];
    const labels = layer.feature_labels ?? [];
    const ranks = layer.feature_depth_ranks ?? [];
    polys.forEach((g, i) => {
      const band = ranks[i] ?? null;
      const color = band ? (FLOOD_DEPTH_COLOR[band] ?? fallbackColor) : fallbackColor;
      if (band) bandsSeen.add(band);
      const depthText = band ? `浸水深 ${FLOOD_DEPTH_LABEL[band]}` : (labels[i] ?? layer.value_label ?? '');
      cells.push({ data: g, band, color, label: depthText ? `${layer.name}: ${depthText}` : layer.name });
    });
    const marker = nearestMarker(layer, fallbackColor);
    if (marker) mks.push(marker);
  }
  // 深い区域ほど後（＝上）に描画して、重なっても浅い区域に隠れないようにする。
  cells.sort((a, b) => (a.band ?? 0) - (b.band ?? 0));
  const geos: GeometryLayer[] = cells.map((c) => ({
    data: c.data,
    style: { stroke: false, fillColor: c.color, fillOpacity: WATER_FILL_OPACITY },
    label: c.label,
  }));
  const legend: LegendItem[] = [...bandsSeen]
    .sort((a, b) => a - b)
    .map((band) => ({ color: FLOOD_DEPTH_COLOR[band], label: FLOOD_DEPTH_LABEL[band] }));
  return { geometries: geos, markers: mks, legend };
}

function statusFromLayer(layer: ReinfoLayerSummary) {
  if (layer.matched_count > 0) {
    return { status: 'alert' as const, label: rankToStatusLabel(layer.rank, '要確認') };
  }
  return { status: 'safe' as const, label: '該当なし' };
}

export type HazardsContent = {
  lead: LeadSpec;
  maps: MapSpec[];
  rows: RowSpec[];
  checkItems: string[];
  /** レイヤが 1 つも無いときの説明文 (rows の代わりに出す)。 */
  emptyText: string | null;
};

// 災害章の中身 (地図・状態行・確認事項)。画面 (ChapterHazards) と PDF 冊子が共有する純関数。
export function buildHazardsContent(portfolio: ReinfoPortfolio, site: Point): HazardsContent {
  const grouped: Record<HazardGroup, ReinfoLayerSummary[]> = { flood: [], landslide: [] };
  for (const layer of portfolio.layers) {
    const group = HAZARD_GROUP_FOR_ENDPOINT[layer.endpoint];
    if (group) grouped[group].push(layer);
  }

  const maps: MapSpec[] = [];
  // 洪水・高潮・津波はそれぞれ別マップ。公式ラスタータイルで面を描画する層は、地点付近にデータが
  // ある（中心タイルに該当 or 交差）ときだけ表示する（内陸の高潮・津波など空振りの地図を出さない）。
  for (const layer of grouped.flood) {
    if (!WATER_RASTER_URL[layer.endpoint]) continue;
    if (!((layer.feature_count ?? 0) > 0 || layer.matched_count > 0)) continue;
    maps.push({
      key: `water-${layer.endpoint}`,
      point: site,
      tileUrl: TILES.pale,
      overlayTileUrl: WATER_RASTER_URL[layer.endpoint],
      zoom: 15,
      height: SUBMAP_HEIGHT,
      geometries: [],
      markers: [],
      legend: FLOOD_DEPTH_LEGEND,
      caption: WATER_MAP_CAPTION[layer.endpoint] ?? layer.name,
      emptyMessage: HAZARD_GROUP_META.flood.emptyMessage,
    });
  }
  // 公式ラスターが無い水害層（将来追加分）は従来どおりバックエンドのポリゴンで描画するフォールバック。
  for (const layer of grouped.flood) {
    if (WATER_RASTER_URL[layer.endpoint] || (layer.feature_geometries?.length ?? 0) === 0) continue;
    const build = buildWaterSubmap([layer]);
    maps.push({
      key: `water-poly-${layer.endpoint}`,
      point: site,
      tileUrl: TILES.pale,
      zoom: 15,
      height: SUBMAP_HEIGHT,
      ...build,
      caption: WATER_MAP_CAPTION[layer.endpoint] ?? layer.name,
      emptyMessage: HAZARD_GROUP_META.flood.emptyMessage,
    });
  }
  if (grouped.landslide.length > 0) {
    const build = buildHazardSubmap(grouped.landslide);
    maps.push({
      key: 'landslide',
      point: site,
      tileUrl: TILES.pale,
      zoom: 15,
      height: SUBMAP_HEIGHT,
      ...build,
      caption: HAZARD_GROUP_META.landslide.caption,
      emptyMessage: HAZARD_GROUP_META.landslide.emptyMessage,
    });
  }

  const rows: RowSpec[] = portfolio.layers.map((layer) => {
    const { status, label } = statusFromLayer(layer);
    return {
      icon: HAZARD_ICON_FOR[layer.endpoint] ?? 'alert-triangle',
      iconTone: status === 'alert' ? 'alert' : 'safe',
      label: layer.name,
      detail:
        layer.value_label ??
        (status === 'safe'
          ? '指定区域に該当する記録はありません'
          : `該当 ${layer.matched_count} 件 / タイル全 ${layer.feature_count} 件`),
      status,
      statusLabel: label,
    };
  });

  return {
    lead: { label: portfolio.label, message: portfolio.buyer_message },
    maps,
    rows,
    checkItems: portfolio.check_items,
    emptyText:
      portfolio.layers.length === 0
        ? '災害ハザードに関するデータを取得できませんでした。お住まいの自治体のハザードマップを併せてご確認ください。'
        : null,
  };
}

export default function ChapterHazards({ portfolio, site }: Props) {
  const content = useMemo(() => buildHazardsContent(portfolio, site), [portfolio, site]);

  return (
    <ChapterSection id="hazards">
      <ChapterLead {...content.lead} />

      {content.maps.map((spec) => (
        <ChapterMap key={spec.key} spec={spec} />
      ))}

      {content.emptyText ? (
        <EmptyState>{content.emptyText}</EmptyState>
      ) : (
        <RowList rows={content.rows} />
      )}

      <CheckList items={content.checkItems} />
      <RawDetails portfolio={portfolio} />
    </ChapterSection>
  );
}
